import React, { useEffect, useMemo, useState } from 'react';
import { API, Auth } from 'aws-amplify';
import gql from 'graphql-tag';
import { Text, View } from '@aws-amplify/ui-react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import './orgList.css';
import FeaturesTable from './FeaturesTable';
import CSDashboard from './CSDashboard';
import OrgCloner from './OrgCloner';
import UserStatus from './UserStatus';
import {
  Box,
  List,
  ListItem,
  CircularProgress,
  Typography,
  Button,
} from '@mui/material';

const fetchOrgs = async () => {
  // Check access first
  const cognitoUser = await Auth.currentAuthenticatedUser();
  const groups =
    cognitoUser.signInUserSession?.accessToken?.payload?.['cognito:groups'] ||
    [];
  if (!groups.includes('paiv_admin_rw')) {
    throw new Error('ACCESS_DENIED');
  }

  let hasNextPage = true;
  let nextToken = null;
  let allItems = [];

  while (hasNextPage) {
    const myQuery = gql`
      query MyQuery($limit: Int, $nextToken: String) {
        listOrgs(limit: $limit, nextToken: $nextToken) {
          items {
            id
            configParms
            name
          }
          nextToken
        }
      }
    `;

    const res = await API.graphql({
      query: myQuery,
      variables: {
        limit: 100,
        nextToken,
      },
    });

    const { items, nextToken: newNextToken } = res.data.listOrgs;
    allItems = allItems.concat(items);
    nextToken = newNextToken;
    hasNextPage = !!nextToken;
  }

  allItems.forEach((org) => {
    org.configParms = JSON.parse(org.configParms);
    org.featureFlags = org.configParms.featureFlags;
    org.features = org.configParms.features;
  });

  return allItems;
};

const filterSearch = (org, term) => {
  const regex = new RegExp(term, 'gi');
  return regex.test(org.id) || regex.test(org.name);
};

export default function OrgsList() {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('list');
  const [searchInput, setSearchInput] = useState('');
  const [focusedOrg, setFocusedOrg] = useState(null);

  const {
    data: orgsList = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['orgs'],
    queryFn: fetchOrgs,
    staleTime: 5 * 60 * 1000,
  });

  // Redirect on access denied
  useEffect(() => {
    if (isError && error?.message === 'ACCESS_DENIED') {
      navigate('/');
    }
  }, [isError, error, navigate]);

  const sortedOrgs = useMemo(() => {
    if (!orgsList.length) return [];
    return orgsList
      .filter((el) => filterSearch(el, searchInput))
      .sort((a, b) => {
        if (a.id < b.id) return -1;
        if (a.id > b.id) return 1;
        return 0;
      });
  }, [orgsList, searchInput]);

  useEffect(() => {
    if (searchInput && sortedOrgs.length < orgsList.length) {
      setFocusedOrg(0);
    } else {
      setFocusedOrg(null);
    }
  }, [searchInput, sortedOrgs.length, orgsList.length]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowDown') {
        if (focusedOrg === null || focusedOrg === undefined) {
          setFocusedOrg(0);
        } else if (focusedOrg < sortedOrgs.length - 1) {
          setFocusedOrg((p) => p + 1);
        }
        return;
      }
      if (e.key === 'ArrowUp' && focusedOrg !== 0 && !isNaN(focusedOrg)) {
        setFocusedOrg((p) => p - 1);
        return;
      }
      if (e.key === 'Enter' && !isNaN(focusedOrg) && sortedOrgs.length) {
        navigate(`/admin-config/${sortedOrgs[focusedOrg].id}`);
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  });

  const isUUID = (str) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str);

  if (isLoading)
    return (
      <CircularProgress
        style={{ position: 'absolute', top: '30vh', left: '50%' }}
      />
    );
  return (
    <View style={{ padding: '100px 50px' }}>
      <Box style={{ display: 'flex', gap: '5px', padding: '15px 0' }}>
        <Button
          onClick={() => setCurrentScreen('list')}
          sx={{
            color: (theme) => theme.custom.text,
            border: '1px solid',
            borderColor: (theme) => theme.custom.text,
          }}
        >
          Orgs List
        </Button>
        <Button
          onClick={() => setCurrentScreen('features')}
          sx={{
            color: (theme) => theme.custom.text,
            border: '1px solid',
            borderColor: (theme) => theme.custom.text,
          }}
        >
          Feature flags
        </Button>
        <Button
          onClick={() => setCurrentScreen('dashboard')}
          sx={{
            color: (theme) => theme.custom.text,
            border: '1px solid',
            borderColor: (theme) => theme.custom.text,
          }}
        >
          CS Dashboard
        </Button>
        <Button
          onClick={() => setCurrentScreen('clone')}
          sx={{
            color: (theme) => theme.custom.text,
            border: '1px solid',
            borderColor: (theme) => theme.custom.text,
          }}
        >
          Clone Org
        </Button>
        <Button
          onClick={() => setCurrentScreen('user-status')}
          sx={{
            color: (theme) => theme.custom.text,
            border: '1px solid',
            borderColor: (theme) => theme.custom.text,
          }}
        >
          User Status
        </Button>
      </Box>
      {currentScreen === 'list' && (
        <Box>
          <Box id="list-header">
            <Typography
              style={{ fontWeight: 'bold', fontSize: '1.6rem' }}
              sx={{ color: (theme) => theme.custom.text }}
            >
              Org List
            </Typography>
            <input
              autoFocus
              autoComplete="off"
              id="org-search-input"
              type="text"
              placeholder="Search org"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </Box>
          <List
            style={{ display: 'flex', flexDirection: 'column', color: 'white' }}
          >
            {sortedOrgs.length ? (
              sortedOrgs.map((el, i) => (
                <ListItem
                  key={i}
                  className="org-tile"
                  style={{
                    backgroundColor: i === focusedOrg ? 'lightblue' : 'white',
                  }}
                  // onClick={() => navigate(`/admin-config/${el.id}`)}
                  onClick={() => navigate(`/admin-config/${el.id}`)}
                  // onClick={() => console.log(el)}
                >
                  <Text className="org-tile">
                    <span>
                      id:{' '}
                      <span style={{ color: '#1976d2', fontWeight: 600 }}>
                        {isUUID(el.id) ? 'uuid' : el.id}
                      </span>
                    </span>
                    <span style={{ color: '#666', margin: '0 6px' }}>-</span>
                    <span>
                      org name:{' '}
                      <span style={{ color: '#1976d2', fontWeight: 600 }}>
                        {el.name}
                      </span>
                    </span>
                  </Text>
                </ListItem>
              ))
            ) : (
              <Text>{`No match to org name ${searchInput} found!`}</Text>
            )}
          </List>
        </Box>
      )}
      {currentScreen === 'features' && (
        <Box>
          <FeaturesTable orgsList={orgsList} />
        </Box>
      )}
      {currentScreen === 'dashboard' && (
        <Box>
          <CSDashboard orgsList={orgsList} />
        </Box>
      )}
      {currentScreen === 'clone' && (
        <Box>
          <OrgCloner orgsList={orgsList} />
        </Box>
      )}
      {currentScreen === 'user-status' && (
        <Box>
          <UserStatus />
        </Box>
      )}
    </View>
  );
}
